'use client'

import { useEffect, useRef, useCallback } from 'react'

interface ElementScrollOptions {
  speed?: number // Scroll speed multiplier (0.5 = half speed, 2 = double speed)
  lag?: number // Lag time in seconds for catch-up effect
  offset?: number // Offset from element position
  easing?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'cubic' | 'expo'
}

// Easing functions
const easingFunctions = {
  linear: (t: number) => t,
  easeIn: (t: number) => t * t,
  easeOut: (t: number) => 1 - Math.pow(1 - t, 2),
  easeInOut: (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
  cubic: (t: number) => 1 - Math.pow(1 - t, 3),
  expo: (t: number) => t === 0 ? 0 : Math.pow(2, 10 * t - 10)
}

export default function useElementSmoothScroll() {
  const elementsRef = useRef<Map<HTMLElement, ElementScrollOptions>>(new Map())
  const animationFrameRef = useRef<number | null>(null)
  const lastScrollTopRef = useRef<number>(0)

  // Parse data attributes from elements
  const parseElementOptions = useCallback((element: HTMLElement): ElementScrollOptions => {
    return {
      speed: parseFloat(element.getAttribute('data-speed') || '1'),
      lag: parseFloat(element.getAttribute('data-lag') || '0'),
      offset: parseFloat(element.getAttribute('data-offset') || '0'),
      easing: (element.getAttribute('data-easing') as any) || 'cubic'
    }
  }, [])

  // Apply parallax effect to elements
  const applyParallax = useCallback(() => {
    const container = document.querySelector('.smooth-content') as HTMLElement
    if (!container) return

    const scrollTop = container.scrollTop
    const containerHeight = container.clientHeight

    elementsRef.current.forEach((options, element) => {
      const rect = element.getBoundingClientRect()
      const elementTop = rect.top + scrollTop
      const elementHeight = rect.height

      // Calculate parallax position
      let translateY = 0

      const speed = options.speed ?? 1
      const lag = options.lag ?? 0
      const easing = options.easing ?? 'cubic'

      if (speed !== 1) {
        // Speed-based parallax (like ScrollSmoother)
        const speedDiff = 1 - speed
        translateY = scrollTop * speedDiff
      }

      if (lag > 0) {
        // Lag-based catch-up effect
        const lagProgress = Math.min(1, (scrollTop - lastScrollTopRef.current) / (lag * 1000))
        translateY += (scrollTop - lastScrollTopRef.current) * lag * lagProgress
      }

      // Apply transform with easing
      const easingFunction = easingFunctions[easing]
      const easedTranslateY = translateY * easingFunction(Math.min(1, Math.abs(translateY) / containerHeight))

      element.style.transform = `translateY(${easedTranslateY}px)`
    })

    lastScrollTopRef.current = scrollTop
  }, [])

  // Initialize element tracking
  const initializeElements = useCallback(() => {
    const elements = document.querySelectorAll('[data-speed], [data-lag], [data-offset], [data-easing]')

    elements.forEach((element) => {
      const options = parseElementOptions(element as HTMLElement)
      elementsRef.current.set(element as HTMLElement, options)
    })
  }, [parseElementOptions])

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    animationFrameRef.current = requestAnimationFrame(applyParallax)
  }, [applyParallax])

  // Initialize
  useEffect(() => {
    const container = document.querySelector('.smooth-content') as HTMLElement
    if (!container) return

    // Initialize elements
    initializeElements()

    // Add scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true })

    // Handle dynamic content changes
    const observer = new MutationObserver(() => {
      initializeElements()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      observer.disconnect()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [initializeElements, handleScroll])

  // Public API for manual element registration
  const registerElement = useCallback((element: HTMLElement, options: ElementScrollOptions) => {
    elementsRef.current.set(element, options)
  }, [])

  const unregisterElement = useCallback((element: HTMLElement) => {
    elementsRef.current.delete(element)
  }, [])

  return {
    registerElement,
    unregisterElement,
    applyParallax
  }
}


